"use client";

import React from "react";
import { Leaf, Sparkles, Loader, Check } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function JoinCommunityForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="mb-6 flex size-16 items-center justify-center rounded-2xl bg-primary/20 text-primary shadow-md">
          <Check className="size-8" />
        </div>
        <h3 className="font-default mb-2 text-2xl font-semibold text-foreground">
          Welcome to the Pack!
        </h3>
        <p className="mb-8 text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-lg shadow-sm border-primary text-primary hover:bg-primary/10">
          Register Another Pet
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="full_name" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Pet Owner Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="border-x-0 border-t-0 border-b-2 border-input rounded-md bg-transparent px-1 shadow-sm focus-visible:border-primary focus-visible:ring-0 transition-colors" {...field} />
              </FormControl>
              <FormDescription>Enter your full legal name.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="border-x-0 border-t-0 border-b-2 border-input rounded-md bg-transparent px-1 shadow-sm focus-visible:border-primary focus-visible:ring-0 transition-colors" {...field} />
              </FormControl>
              <FormDescription>We'll send health tips to this inbox.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <FormField control={control} name="password" rules={{
        required: "Password is required",
        minLength: {
          value: 8,
          message: "Minimum 8 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Portal Password</FormLabel>
              <FormControl>
                <Input type="password" placeholder="Secure your account" className="border-x-0 border-t-0 border-b-2 border-input rounded-md bg-transparent px-1 shadow-sm focus-visible:border-primary focus-visible:ring-0 transition-colors" {...field} />
              </FormControl>
              <FormDescription>Minimum 8 characters required.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="language_preference" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-default">Preferred Language</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-9 w-full appearance-none items-center justify-between border-x-0 border-t-0 border-b-2 border-input rounded-md bg-transparent px-1 py-2 text-base shadow-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm transition-colors" {...field}>
                    {["English", "Spanish", "French"].map((lang, i) => <option key={lang} value={lang} data-index={i} className="bg-card text-foreground">
                        {lang}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="size-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="agree_terms" rules={{
      required: "You must accept the terms to join."
    }} render={({
      field: {
        value,
        onChange,
        ...field
      }
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-lg border border-border/50 p-4 shadow-sm bg-background/40">
            <FormControl>
              <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="mt-1 size-4 shrink-0 rounded-md border border-primary text-primary shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 accent-primary" {...field} />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-sm font-medium text-foreground font-default">
                I agree to the Care Terms & Privacy Policy
              </FormLabel>
              <FormDescription className="text-xs">
                Required for medical record access. Read our{" "}
                <Link to="/ContactPage" className="underline hover:text-primary transition-colors">
                  Privacy Policy
                </Link>.
              </FormDescription>
            </div>
          </FormItem>} />

      {submitError && <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 shadow-sm">
          <p className="text-sm text-destructive">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-lg py-6 text-base font-default font-semibold shadow-lg transition-all hover:shadow-xl hover:scale-[1.01] active:scale-[0.99] bg-primary text-primary-foreground">
        {isSubmitting ? <>
            <Loader className="mr-2 size-5 animate-spin" />
            Registering...
          </> : <>
            Join the Community
            <Sparkles className="ml-2 size-4" />
          </>}
      </Button>
    </Form>;
}
export default function StayUpdated() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="263534" id="stay-updated" className="relative overflow-hidden dark bg-background py-24 md:py-32" ref={ref}>
      {}
      <div className="pointer-events-none absolute inset-0 overflow-hidden opacity-[0.05] dark:opacity-[0.08]">
        <Leaf className="absolute -left-12 top-24 size-64 -rotate-45 text-primary" />
        <Leaf className="absolute -right-24 bottom-12 size-96 rotate-12 text-secondary" />
        <Leaf className="absolute left-1/3 top-0 size-48 rotate-90 text-primary" />
      </div>

      <div className="container relative mx-auto px-4 md:px-6">
        <div className={`mx-auto max-w-3xl transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"}`}>
          <div className="rounded-3xl border border-border/40 bg-card/90 p-8 shadow-2xl backdrop-blur-xl md:p-12">
            <div className="mb-10 text-center">
              <h2 className="font-default mb-3 text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-5xl">
                Join Our Paw-Print Newsletter
              </h2>
              <p className="font-default text-lg text-muted-foreground">
                Expert health tips for your furry family members
              </p>
            </div>

            <FormProvider formKey="stay_updated_form_contact" formId="stay_updated_form_contact" sectionName="JoinTheAlMaCommunity" defaultValues={{
            full_name: "",
            email: "",
            password: "",
            language_preference: "English",
            agree_terms: false
          }} submitText="Join Us" successMessage="Welcome to Happy Paws! You're now part of a community dedicated to pet wellness.">
              <JoinCommunityForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}